import {
  CHUNK_SIZE,
  type ConstructionSiteSnapshot,
  type ObjectTypeValue,
  TILE_SIZE,
} from '@dreamgame/shared';
import { Container, Graphics, Sprite } from 'pixi.js';
import {
  constructionSites,
  isLinePlacing,
  isPlacingConstruction,
  lineTiles,
  placementPreviewX,
  placementPreviewY,
  placementValid,
  selectedConstruction,
} from '../../state/construction';
import { getObjectTexture } from './ObjectLayer';
import type { VisibleChunk } from './types';

// ============================================================================
// ConstructionLayer - Renders construction sites and placement preview
// ============================================================================
// Beautiful blueprint-style rendering for construction sites
// Shows progress bars and worker count
// ============================================================================

let container: Container;
let graphics: Graphics;

// Ghost preview sprite for placement
let ghostSprite: Sprite | null = null;
let ghostSpriteObjectType: number = -1;

// PERFORMANCE: Cached bounds to avoid allocation every frame
const _visibleBounds = { minX: 0, maxX: 0, minY: 0, maxY: 0 };

// Colors for construction rendering
const COLORS = {
  blueprintFill: 0x3366ff,
  blueprintStroke: 0x4488ff,
  blueprintFillAlpha: 0.2,
  blueprintStrokeAlpha: 0.6,
  progressBg: 0x222222,
  progressFill: 0x44aaff,
  workerText: 0xffffff,
  placementValid: 0x44ff44,
  placementInvalid: 0xff4444,
  placementAlpha: 0.4,
};

export const createConstructionLayer = (): { container: Container; graphics: Graphics } => {
  container = new Container();
  graphics = new Graphics();
  container.addChild(graphics);
  return { container, graphics };
};

// Find min/max chunk coordinates
const findChunkBounds = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): { minX: number; maxX: number; minY: number; maxY: number } => {
  const first = visibleChunks[0];
  let minX = first?.x ?? 0;
  let maxX = minX;
  let minY = first?.y ?? 0;
  let maxY = minY;

  for (let i = 1; i < visibleChunks.length; i++) {
    const chunk = visibleChunks[i];
    if (chunk) {
      minX = Math.min(minX, chunk.x);
      maxX = Math.max(maxX, chunk.x);
      minY = Math.min(minY, chunk.y);
      maxY = Math.max(maxY, chunk.y);
    }
  }

  return { minX, maxX, minY, maxY };
};

// Calculate visible bounds from chunk list
const updateVisibleBounds = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  if (visibleChunks.length === 0) {
    return;
  }

  const bounds = findChunkBounds({ visibleChunks });
  _visibleBounds.minX = bounds.minX * CHUNK_SIZE;
  _visibleBounds.maxX = (bounds.maxX + 1) * CHUNK_SIZE;
  _visibleBounds.minY = bounds.minY * CHUNK_SIZE;
  _visibleBounds.maxY = (bounds.maxY + 1) * CHUNK_SIZE;
};

// Check if site is in visible range
const isSiteVisible = ({ site }: { site: ConstructionSiteSnapshot }): boolean => {
  const siteMaxX = site.tileX + site.width;
  const siteMaxY = site.tileY + site.height;
  return (
    site.tileX < _visibleBounds.maxX &&
    siteMaxX > _visibleBounds.minX &&
    site.tileY < _visibleBounds.maxY &&
    siteMaxY > _visibleBounds.minY
  );
};

// Draw visible construction sites
const drawVisibleSites = (): void => {
  const sites = constructionSites.value;
  for (const site of sites) {
    if (isSiteVisible({ site })) {
      drawConstructionSite({ site });
    }
  }
};

// Handle placement preview rendering
const handlePlacementPreview = (): void => {
  if (isPlacingConstruction.value && selectedConstruction.value) {
    drawPlacementPreview();
  } else {
    hideGhostSprite();
  }
};

export const drawConstructionSites = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  graphics.clear();
  updateVisibleBounds({ visibleChunks });
  drawVisibleSites();
  handlePlacementPreview();
};

const drawConstructionSite = ({ site }: { site: ConstructionSiteSnapshot }): void => {
  const worldX = site.tileX * TILE_SIZE;
  const worldY = site.tileY * TILE_SIZE;
  const width = site.width * TILE_SIZE;
  const height = site.height * TILE_SIZE;

  // Draw blueprint background
  graphics.setFillStyle({ color: COLORS.blueprintFill, alpha: COLORS.blueprintFillAlpha });
  graphics.rect(worldX, worldY, width, height);
  graphics.fill();

  // Draw blueprint border (dashed effect using small segments)
  graphics.setStrokeStyle({
    width: 2,
    color: COLORS.blueprintStroke,
    alpha: COLORS.blueprintStrokeAlpha,
  });
  drawDashedRect({ x: worldX, y: worldY, width, height, dashLength: 4 });

  // Draw diagonal lines pattern for blueprint effect
  drawBlueprintPattern({ x: worldX, y: worldY, width, height });

  // Draw progress bar
  drawProgressBar({
    x: worldX + 4,
    y: worldY + height - 8,
    width: width - 8,
    height: 4,
    progress: site.progress,
  });

  // Draw worker count if workers are assigned
  if (site.workersAssigned > 0) {
    drawWorkerCount({
      x: worldX + width / 2,
      y: worldY + height / 2,
      _count: site.workersAssigned,
    });
  }

  // Draw percentage text
  const percent = Math.floor(site.progress * 100);
  if (percent > 0) {
    graphics.setFillStyle({ color: 0xffffff, alpha: 0.9 });
    // Draw simple percentage indicator using shapes
    const textX = worldX + width / 2;
    const textY = worldY + 8;
    drawPercentageIndicator({ x: textX, y: textY, percent });
  }
};

const drawDashedRect = ({
  x,
  y,
  width,
  height,
  dashLength,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  dashLength: number;
}): void => {
  // Top edge
  for (let dx = 0; dx < width; dx += dashLength * 2) {
    graphics.moveTo(x + dx, y);
    graphics.lineTo(x + Math.min(dx + dashLength, width), y);
    graphics.stroke();
  }

  // Right edge
  for (let dy = 0; dy < height; dy += dashLength * 2) {
    graphics.moveTo(x + width, y + dy);
    graphics.lineTo(x + width, y + Math.min(dy + dashLength, height));
    graphics.stroke();
  }

  // Bottom edge
  for (let dx = 0; dx < width; dx += dashLength * 2) {
    graphics.moveTo(x + width - dx, y + height);
    graphics.lineTo(x + width - Math.min(dx + dashLength, width), y + height);
    graphics.stroke();
  }

  // Left edge
  for (let dy = 0; dy < height; dy += dashLength * 2) {
    graphics.moveTo(x, y + height - dy);
    graphics.lineTo(x, y + height - Math.min(dy + dashLength, height));
    graphics.stroke();
  }
};

const drawBlueprintPattern = ({
  x,
  y,
  width,
  height,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
}): void => {
  graphics.setStrokeStyle({ width: 1, color: COLORS.blueprintStroke, alpha: 0.2 });

  // Diagonal lines
  const spacing = 8;
  for (let offset = -height; offset < width + height; offset += spacing) {
    const x1 = Math.max(0, offset);
    const y1 = Math.max(0, -offset);
    const x2 = Math.min(width, width + offset);
    const y2 = Math.min(height, height - offset + width);

    if (x1 < width && y1 < height) {
      graphics.moveTo(x + x1, y + y1);
      graphics.lineTo(x + Math.min(x2, width), y + Math.min(y2, height));
      graphics.stroke();
    }
  }
};

const drawProgressBar = ({
  x,
  y,
  width,
  height,
  progress,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  progress: number;
}): void => {
  // Background
  graphics.setFillStyle({ color: COLORS.progressBg, alpha: 0.8 });
  graphics.roundRect(x, y, width, height, 2);
  graphics.fill();

  // Progress fill
  if (progress > 0) {
    const progressWidth = Math.max(2, width * progress);
    graphics.setFillStyle({ color: COLORS.progressFill });
    graphics.roundRect(x, y, progressWidth, height, 2);
    graphics.fill();
  }

  // Border
  graphics.setStrokeStyle({ width: 1, color: 0x666666 });
  graphics.roundRect(x, y, width, height, 2);
  graphics.stroke();
};

const drawWorkerCount = ({ x, y, _count }: { x: number; y: number; _count: number }): void => {
  // Draw a small circle with worker count
  const radius = 8;
  graphics.setFillStyle({ color: 0x444444, alpha: 0.9 });
  graphics.circle(x, y, radius);
  graphics.fill();
  graphics.setStrokeStyle({ width: 1, color: 0x888888 });
  graphics.circle(x, y, radius);
  graphics.stroke();

  // Draw hammer icon or simple indicator
  graphics.setFillStyle({ color: 0xffffff });
  graphics.circle(x, y, 2);
  graphics.fill();
};

const drawPercentageIndicator = ({
  x,
  y,
  percent,
}: {
  x: number;
  y: number;
  percent: number;
}): void => {
  // Simple progress indicator using dots
  const dotsTotal = 10;
  const dotsFilled = Math.floor(percent / 10);
  const dotSize = 2;
  const spacing = 3;
  const totalWidth = dotsTotal * (dotSize + spacing) - spacing;
  const startX = x - totalWidth / 2;

  for (let i = 0; i < dotsTotal; i++) {
    const dotX = startX + i * (dotSize + spacing);
    if (i < dotsFilled) {
      graphics.setFillStyle({ color: COLORS.progressFill });
    } else {
      graphics.setFillStyle({ color: 0x444444 });
    }
    graphics.circle(dotX + dotSize / 2, y, dotSize / 2);
    graphics.fill();
  }
};

const drawPlacementPreview = (): void => {
  const definition = selectedConstruction.value;
  if (!definition) {
    hideGhostSprite();
    return;
  }

  // If line placing, draw the line preview instead
  if (isLinePlacing.value) {
    hideGhostSprite();
    drawLinePlacementPreview();
    return;
  }

  const tileX = placementPreviewX.value;
  const tileY = placementPreviewY.value;
  const isValid = placementValid.value;

  // Draw ghost sprite for single placement
  drawGhostSprite({
    tileX,
    tileY,
    objectType: definition.objectType,
    width: definition.width,
    height: definition.height,
    isValid,
  });

  // Draw subtle border overlay
  drawSinglePlacementTile({
    tileX,
    tileY,
    width: definition.width,
    height: definition.height,
    isValid,
    showCrosshair: false,
  });
};

const drawGhostSprite = ({
  tileX,
  tileY,
  objectType,
  width,
  height,
  isValid,
}: {
  tileX: number;
  tileY: number;
  objectType: ObjectTypeValue;
  width: number;
  height: number;
  isValid: boolean;
}): void => {
  const texture = getObjectTexture({ objectType });

  // If no texture, fall back to showing just the colored overlay
  if (!texture) {
    hideGhostSprite();
    return;
  }

  // Create or update ghost sprite
  if (!ghostSprite || ghostSpriteObjectType !== objectType) {
    // Remove old sprite if exists
    if (ghostSprite) {
      container.removeChild(ghostSprite);
      ghostSprite.destroy();
    }

    // Create new sprite
    ghostSprite = new Sprite(texture);
    ghostSprite.width = width * TILE_SIZE;
    ghostSprite.height = height * TILE_SIZE;
    container.addChild(ghostSprite);
    ghostSpriteObjectType = objectType;
  }

  // Position the ghost sprite
  ghostSprite.x = tileX * TILE_SIZE;
  ghostSprite.y = tileY * TILE_SIZE;
  ghostSprite.visible = true;

  // Apply strong ghost/blueprint effect
  // Very blue tint for valid, red tint for invalid
  ghostSprite.tint = isValid ? 0x2288ff : 0xff4444;
  ghostSprite.alpha = 0.5;
};

const hideGhostSprite = (): void => {
  if (ghostSprite) {
    ghostSprite.visible = false;
  }
};

// Draw the count indicator badge for line placement
const drawLineCountIndicator = ({
  tiles,
  validCount,
}: {
  tiles: ReadonlyArray<{ x: number; y: number; valid: boolean }>;
  validCount: number;
}): void => {
  if (tiles.length === 0) {
    return;
  }
  const firstTile = tiles[0];
  const lastTile = tiles[tiles.length - 1];
  if (!firstTile || !lastTile) {
    return;
  }
  const midX = ((firstTile.x + lastTile.x) / 2) * TILE_SIZE + TILE_SIZE / 2;
  const midY = ((firstTile.y + lastTile.y) / 2) * TILE_SIZE - 12;

  // Draw count background
  graphics.setFillStyle({ color: 0x222222, alpha: 0.9 });
  graphics.roundRect(midX - 16, midY - 8, 32, 16, 4);
  graphics.fill();

  // Draw border
  graphics.setStrokeStyle({ width: 1, color: validCount > 0 ? 0x44ff44 : 0xff4444 });
  graphics.roundRect(midX - 16, midY - 8, 32, 16, 4);
  graphics.stroke();

  // Draw count as dots (simple visual)
  const dotSpacing = 4;
  const maxDots = Math.min(validCount, 7);
  const startX = midX - ((maxDots - 1) * dotSpacing) / 2;
  graphics.setFillStyle({ color: 0x44ff44 });
  for (let i = 0; i < maxDots; i++) {
    graphics.circle(startX + i * dotSpacing, midY, 2);
    graphics.fill();
  }
  if (validCount > 7) {
    graphics.setFillStyle({ color: 0x44ff44 });
    graphics.circle(midX + 10, midY, 1);
    graphics.fill();
  }
};

const drawLinePlacementPreview = (): void => {
  const tiles = lineTiles.value;

  // PERFORMANCE: Count valid tiles in same loop as drawing (avoids .filter() allocation)
  let validCount = 0;
  for (const tile of tiles) {
    drawSinglePlacementTile({
      tileX: tile.x,
      tileY: tile.y,
      width: 1,
      height: 1,
      isValid: tile.valid,
      showCrosshair: false,
    });
    if (tile.valid) {
      validCount++;
    }
  }
  drawLineCountIndicator({ tiles, validCount });
};

const drawSinglePlacementTile = ({
  tileX,
  tileY,
  width,
  height,
  isValid,
  showCrosshair,
}: {
  tileX: number;
  tileY: number;
  width: number;
  height: number;
  isValid: boolean;
  showCrosshair: boolean;
}): void => {
  const worldX = tileX * TILE_SIZE;
  const worldY = tileY * TILE_SIZE;
  const pixelWidth = width * TILE_SIZE;
  const pixelHeight = height * TILE_SIZE;

  const color = isValid ? COLORS.placementValid : COLORS.placementInvalid;

  // Draw subtle border only (ghost sprite shows the actual preview)
  graphics.setStrokeStyle({ width: 2, color, alpha: 0.9 });
  graphics.rect(worldX + 1, worldY + 1, pixelWidth - 2, pixelHeight - 2);
  graphics.stroke();

  // Draw corner markers for extra clarity
  const cornerSize = 6;
  graphics.setStrokeStyle({ width: 2, color, alpha: 1 });

  // Top-left
  graphics.moveTo(worldX, worldY + cornerSize);
  graphics.lineTo(worldX, worldY);
  graphics.lineTo(worldX + cornerSize, worldY);
  graphics.stroke();

  // Top-right
  graphics.moveTo(worldX + pixelWidth - cornerSize, worldY);
  graphics.lineTo(worldX + pixelWidth, worldY);
  graphics.lineTo(worldX + pixelWidth, worldY + cornerSize);
  graphics.stroke();

  // Bottom-left
  graphics.moveTo(worldX, worldY + pixelHeight - cornerSize);
  graphics.lineTo(worldX, worldY + pixelHeight);
  graphics.lineTo(worldX + cornerSize, worldY + pixelHeight);
  graphics.stroke();

  // Bottom-right
  graphics.moveTo(worldX + pixelWidth - cornerSize, worldY + pixelHeight);
  graphics.lineTo(worldX + pixelWidth, worldY + pixelHeight);
  graphics.lineTo(worldX + pixelWidth, worldY + pixelHeight - cornerSize);
  graphics.stroke();

  // Draw crosshair only if requested (for line placement fallback)
  if (showCrosshair) {
    const centerX = worldX + pixelWidth / 2;
    const centerY = worldY + pixelHeight / 2;
    const crossSize = 6;

    graphics.setStrokeStyle({ width: 2, color: 0xffffff, alpha: 0.8 });
    graphics.moveTo(centerX - crossSize, centerY);
    graphics.lineTo(centerX + crossSize, centerY);
    graphics.stroke();
    graphics.moveTo(centerX, centerY - crossSize);
    graphics.lineTo(centerX, centerY + crossSize);
    graphics.stroke();
  }
};
